/**
 * Core Typography Tokens — Tier 1
 *
 * Raw typographic primitives. No semantic meaning.
 * JS key maps to Figma token: fontWeight.bold → font-weight-bold, fontSize[16] → font-size-16.
 *
 * MUI mapping (done in theme config, not here):
 * - fontFamily  → theme.typography.fontFamily
 * - fontWeight  → theme.typography.[variant].fontWeight
 * - fontSize    → theme.typography.[variant].fontSize
 * - lineHeight  → theme.typography.[variant].lineHeight
 * - letterSpacing → theme.typography.[variant].letterSpacing
 *
 * Rules:
 * - Tier 2 semantic tokens reference these keys; never raw values
 */

export const fontFamily = {
  primary: 'Roboto',
  fallback: 'sans-serif',
} as const;

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const fontSize = {
  10: 10,
  12: 12,
  14: 14,
  15: 15,
  16: 16,
  20: 20,
  24: 24,
  34: 34,
  48: 48,
  60: 60,
  96: 96,
} as const;

export const lineHeight = {
  // Semantic scale — use these for non-variant-specific contexts
  tight: 116.7,
  snug: 120,
  normal: 150,
  relaxed: 175,
  loose: 266,
  // Per-variant aliases — map directly to MUI typography variants
  h1: 116.7,
  h2: 120,
  h3: 116.7,
  h4: 123.5,
  h5: 133.4,
  h6: 160,
  subtitle1: 150,
  subtitle2: 143,
  body1: 175,
  body2: 157,
  caption: 266,
  overline: 166,
} as const;

export const letterSpacing = {
  'tight-2': -1.5,
  'tight-1': -0.5,
  none: 0,
  xs: 0.1,
  sm: 0.15,
  md: 0.17,
  lg: 0.25,
  xl: 1,
} as const;

export type FontFamily = typeof fontFamily;
export type FontFamilyToken = keyof FontFamily;

export type FontWeight = typeof fontWeight;
export type FontWeightToken = keyof FontWeight;

export type FontSize = typeof fontSize;
export type FontSizeToken = keyof FontSize;

export type LineHeight = typeof lineHeight;
export type LineHeightToken = keyof LineHeight;

export type LetterSpacing = typeof letterSpacing;
export type LetterSpacingToken = keyof LetterSpacing;
